import React from "react";
import { X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const generateCaptcha = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let value = "";
  for (let i = 0; i < 6; i += 1) {
    value += chars[Math.floor(Math.random() * chars.length)];
  }
  return value;
};

type LeadCaptureModalProps = {
  open: boolean;
  onClose: () => void;
};

export const LeadCaptureModal = ({ open, onClose }: LeadCaptureModalProps) => {
  const [captchaCode, setCaptchaCode] = React.useState(generateCaptcha());
  const [form, setForm] = React.useState({
    fullName: "",
    company: "",
    website: "",
    designation: "",
    email: "",
    mobile: "",
    projectGoals: "",
    captcha: "",
  });

  React.useEffect(() => {
    if (open) {
      setCaptchaCode(generateCaptcha());
      setForm({
        fullName: "",
        company: "",
        website: "",
        designation: "",
        email: "",
        mobile: "",
        projectGoals: "",
        captcha: "",
      });
    }
  }, [open]);

  if (!open) {
    return null;
  }

  const updateField = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (form.captcha.trim().toUpperCase() !== captchaCode) {
      toast.error("Captcha does not match. Please try again.");
      setCaptchaCode(generateCaptcha());
      setForm((prev) => ({ ...prev, captcha: "" }));
      return;
    }

    try {
      const response = await fetch('https://ecoclubsbackend.aicte-india.org/api/contacts', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fullName: form.fullName,
          workEmail: form.email,
          company: form.company,
          companyWebsite: form.website,
          designation: form.designation,
          mobileNumber: form.mobile,
          projectGoals: form.projectGoals,
        }),
      });

      if (response.ok) {
        toast.success("Thanks! We will reach out shortly.");
        onClose();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Failed to submit. Please try again.");
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error("Network error. Please check your connection and try again.");
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4">
      <button
        type="button"
        aria-label="Close modal"
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative w-full max-w-xs sm:max-w-md lg:max-w-xl rounded-2xl sm:rounded-3xl lg:rounded-[2.5rem] border border-white/20 bg-white shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl lg:rounded-[2.5rem]">
          <div className="absolute inset-0 bg-gradient-to-br from-[#f97316] via-[#db2777] to-[#4f46e5] opacity-15" />
          <div className="relative px-3 py-3 sm:px-5 lg:px-6 lg:py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                
                <h6 className="mt-1 text-sm sm:text-xl lg:text-2xl font-black text-[#3730a3] leading-tight">Share your requirements to work with you. We will keep your project requirements confidential.</h6>
                {/* <p className="mt-1 sm:mt-2 text-xs sm:text-sm text-gray-500">We will connect you with the right Anuvadini expert.</p> */}
              </div>
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-gray-200 bg-white p-1.5 sm:p-2 text-gray-500 shadow-sm hover:text-gray-700 flex-shrink-0"
                aria-label="Close"
              >
                <X size={16} className="sm:w-[18px] sm:h-[18px]" />
              </button>
            </div>

            <form className="mt-3 sm:mt-4 lg:mt-5 space-y-2.5" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 gap-2 sm:gap-3">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Full Name</label>
                  <input
                    required
                    type="text"
                    value={form.fullName}
                    onChange={updateField("fullName")}
                    className="w-full rounded-xl sm:rounded-2xl border border-gray-200 bg-white/90 px-3 py-1.5 sm:px-4 sm:py-2.5 text-sm text-gray-900 shadow-sm focus:border-[#f97316] focus:outline-none"
                    placeholder="Your full name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={updateField("company")}
                    className="w-full rounded-xl sm:rounded-2xl border border-gray-200 bg-white/90 px-3 py-1.5 sm:px-4 sm:py-2.5 text-sm text-gray-900 shadow-sm focus:border-[#f97316] focus:outline-none"
                    placeholder="Your company name"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Company Website</label>
                  <input
                    type="url"
                    value={form.website}
                    onChange={updateField("website")}
                    className="w-full rounded-xl sm:rounded-2xl border border-gray-200 bg-white/90 px-3 py-1.5 sm:px-4 sm:py-2.5 text-sm text-gray-900 shadow-sm focus:border-[#f97316] focus:outline-none"
                    placeholder="www.yourcompany.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Designation</label>
                  <input
                    type="text"
                    value={form.designation}
                    onChange={updateField("designation")}
                    className="w-full rounded-xl sm:rounded-2xl border border-gray-200 bg-white/90 px-3 py-1.5 sm:px-4 sm:py-2.5 text-sm text-gray-900 shadow-sm focus:border-[#f97316] focus:outline-none"
                    placeholder="Your job title"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Mobile Number</label>
                  <input
                    required
                    type="tel"
                    value={form.mobile}
                    onChange={updateField("mobile")}
                    className="w-full rounded-xl sm:rounded-2xl border border-gray-200 bg-white/90 px-3 py-1.5 sm:px-4 sm:py-2.5 text-sm text-gray-900 shadow-sm focus:border-[#f97316] focus:outline-none"
                    placeholder="Your phone number"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-2 sm:gap-3">
                <div>
                  <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Work Email</label>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={updateField("email")}
                    className="w-full rounded-xl sm:rounded-2xl border border-gray-200 bg-white/90 px-3 py-1.5 sm:px-4 sm:py-2.5 text-sm text-gray-900 shadow-sm focus:border-[#f97316] focus:outline-none"
                    placeholder="your.email@company.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">Project Goals / Requirements</label>
                <textarea
                  value={form.projectGoals}
                  onChange={updateField("projectGoals")}
                  rows={2}
                  className="w-full rounded-xl sm:rounded-2xl border border-gray-200 bg-white/90 px-3 py-1.5 sm:px-4 sm:py-2.5 text-sm text-gray-900 shadow-sm focus:border-[#f97316] focus:outline-none resize-none"
                  placeholder="Tell us about your project requirements"
                />
              </div>

              <div className="rounded-xl sm:rounded-2xl border border-gray-100 bg-white/80 p-2 sm:p-3">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="rounded-xl sm:rounded-2xl bg-gradient-to-r from-[#f97316] via-[#db2777] to-[#4f46e5] px-2 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm font-black tracking-[0.2em] sm:tracking-[0.3em] text-white shadow-lg flex-shrink-0">
                    {captchaCode}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCaptchaCode(generateCaptcha())}
                    className="p-1.5 sm:p-2 text-[#3730a3] hover:text-[#f97316] hover:bg-gray-50 rounded-lg transition-colors flex-shrink-0"
                    aria-label="Refresh captcha"
                  >
                    <RotateCcw size={14} className="sm:w-4 sm:h-4" />
                  </button>
                  <input
                    required
                    type="text"
                    value={form.captcha}
                    onChange={updateField("captcha")}
                    className="flex-1 min-w-0 rounded-xl sm:rounded-2xl border border-gray-200 bg-white px-2 py-1.5 sm:px-3 sm:py-2.5 text-sm text-gray-900 shadow-sm focus:border-[#f97316] focus:outline-none"
                    placeholder="Enter captcha"
                  />
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-1">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto rounded-full border border-gray-200 px-4 py-2 sm:px-6 sm:py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto rounded-full bg-green-400 hover:bg-green-500 px-6 py-2 sm:px-8 sm:py-2.5 text-sm font-black text-white shadow-lg transition-all"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};